<template>
    <div class="login-container">
      <div class="glass-card">
        <h1 class="title">Create Account</h1>
        <p class="subtitle">Sign up to continue</p>
  
        <div v-if="error" class="error-message">{{ error }}</div>
        <div v-if="successMessage" class="success-message">{{ successMessage }}</div>
  
        <form @submit.prevent="handleRegister" v-if="!successMessage">
          <div class="input-wrapper">
            <input type="text" placeholder="Username" class="input-field" v-model="formData.username" required />
          </div>
          <div class="input-wrapper">
            <input type="email" placeholder="Email Address" class="input-field" v-model="formData.email" required />
          </div>
          <div class="input-wrapper password-container">
             <input :type="showPassword ? 'text' : 'password'" placeholder="Password (min 6 chars)" class="input-field" v-model="formData.password" required />
             <span class="toggle-password" @click="showPassword = !showPassword">{{ showPassword ? '👁️' : '👁️‍🗨️' }}</span>
          </div>
           <div class="input-wrapper password-container">
             <input :type="showConfirmPassword ? 'text' : 'password'" placeholder="Confirm Password" class="input-field" v-model="formData.passwordConfirm" required />
             <span class="toggle-password" @click="showConfirmPassword = !showConfirmPassword">{{ showConfirmPassword ? '👁️' : '👁️‍🗨️' }}</span>
          </div>
           <button type="submit" class="login-btn" :disabled="loading">
            {{ loading ? 'Registering...' : 'Sign Up' }}
          </button>
        </form>
  
        <div class="links" style="justify-content: center; margin-top: 20px;">
           <router-link to="/login" class="sign-up">Already have an account? Login</router-link>
         </div>
      </div>
    </div>
  </template>
  
  <script>
  // Option 1: If using Vuex
  import { mapActions } from 'vuex';
  
  // Option 2: If using an API service directly
  // import authService from '@/services/authService'; // Adjust path as needed
  
  export default {
    name: 'UserRegister',
    data() {
      return {
        formData: {
          username: '',
          email: '',
          password: '',
          passwordConfirm: '',
          // profile_title: '', // Uncomment if using
        },
        showPassword: false,
        showConfirmPassword: false,
        loading: false,
        error: null,
        successMessage: null,
      };
    },
    methods: {
      // Option 1: If using Vuex
      ...mapActions(['register']), // Assuming you create a 'register' action in Vuex
  
      async handleRegister() {
        this.error = null;
        this.successMessage = null; // Clear previous success message
        if (this.formData.password !== this.formData.passwordConfirm) {
          this.error = 'Passwords do not match.';
          return;
        }
        if (this.formData.password.length < 6) {
           this.error = 'Password must be at least 6 characters long.';
           return;
        }
  
        this.loading = true;
  
        try {
           const userData = { ...this.formData };
           delete userData.passwordConfirm; // Don't send confirmation field
  
           // Option 1: Call Vuex action
           const response = await this.register(userData);
           // Assuming the action returns the response or throws an error
           // And sets success state or error state globally or locally
           this.successMessage = response?.data?.message || 'Registration successful! Please check your email for verification if needed, then login.';
           // Consider redirecting after a delay or clearing the form
  
           // Option 2: Call API Service directly
           // const response = await authService.register(userData);
           // this.successMessage = response.data.message;
  
           // Optionally clear form on success
           // this.formData = { username: '', email: '', password: '', passwordConfirm: '' };
  
        } catch (err) {
           console.error("Registration Error:", err);
           // Extract error message from backend response if available
           this.error = err?.response?.data?.message || err.message || 'An unknown error occurred during registration.';
        } finally {
          this.loading = false;
        }
      },
    },
  };
  </script>
  
  <style scoped>
  /* Add specific styles or reuse styles from UserLogin.vue */
  .login-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100vh;
    background: linear-gradient(-45deg, #141e30, #243b55, #141e30); /* Same background */
    background-size: 400% 400%;
    animation: gradientAnimation 10s ease infinite; /* Same animation */
  }
  .glass-card {
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(12px);
    border-radius: 12px;
    padding: 40px;
    text-align: center;
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.2);
    width: 380px; /* Slightly wider for more fields */
    max-width: 100%;
  }
  .title { font-size: 24px; color: #fff; font-weight: bold; margin-bottom: 5px; }
  .subtitle { color: rgba(255, 255, 255, 0.8); font-size: 14px; margin-bottom: 20px; }
  .input-wrapper { width: 100%; position: relative; margin-bottom: 15px; }
  .input-field { /* Reusing styles from UserLogin */
     width: 100%;
     max-width: 100%;
     box-sizing: border-box;
     padding: 12px;
     border: none;
     border-radius: 8px;
     font-size: 16px;
     background: rgba(255, 255, 255, 0.2);
     color: #fff;
     outline: none;
  }
  .input-field::placeholder { color: rgba(255, 255, 255, 0.7); }
  .password-container { position: relative; }
  .toggle-password { /* Reusing styles from UserLogin */
     position: absolute;
     right: 12px;
     top: 50%;
     transform: translateY(-50%);
     cursor: pointer;
     font-size: 18px;
     color: rgba(255, 255, 255, 0.7);
  }
  .login-btn { /* Reusing styles from UserLogin */
     width: 100%;
     max-width: 100%;
     padding: 12px;
     font-size: 16px;
     font-weight: 500;
     border: none;
     border-radius: 8px;
     cursor: pointer;
     transition: transform 0.2s, box-shadow 0.2s;
     background: #4CAF50; /* Same as login button */
     color: white;
  }
  .login-btn:disabled { background: #aaa; cursor: not-allowed; }
  .login-btn:hover:enabled { transform: scale(1.05); box-shadow: 0 4px 10px rgba(255, 255, 255, 0.3); }
  .links { display: flex; justify-content: space-between; margin-top: 15px; font-size: 14px; }
  .sign-up { color: rgba(255, 255, 255, 0.8); text-decoration: none; transition: color 0.3s; }
  .sign-up:hover { color: #4CAF50; }
  .error-message {
    background-color: rgba(255, 0, 0, 0.2);
    color: #ffdddd;
    padding: 10px;
    border-radius: 5px;
    margin-bottom: 15px;
    border: 1px solid rgba(255, 0, 0, 0.3);
    font-size: 14px;
  }
  .success-message {
     background-color: rgba(0, 255, 0, 0.15);
     color: #ddffdd;
     padding: 10px;
     border-radius: 5px;
     margin-bottom: 15px;
     border: 1px solid rgba(0, 255, 0, 0.2);
     font-size: 14px;
   }
  
  @keyframes gradientAnimation { /* Same animation */
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
  </style>